import { useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import {
  formatModelLabel,
  isGatewayRoutedModelId,
  MODEL_SWITCHABLE_STATUSES,
} from '@reflex/shared';
import { useUpdateAgentRuntimeSettings } from '@/hooks/agents/useAgents';
import { useAgentModelSupportHelpers } from '@/hooks/config/useAgentModelSupport';
import { useAgentTypeRegistry } from '@/lib/agent-type-registry';
import {
  MODEL_SWITCH_UNAVAILABLE,
  modelSwitchErrorMessage,
} from '@/components/agent/agent-detail/model-switch-copy';
import type { Agent, AgentType } from '@/lib/types';

export interface ModelSwitch {
  canSwitch: boolean;
  /** Tooltip for the disabled chip. Unset while the catalog is unknown. */
  unavailableReason: string | undefined;
  options: { id: string; name: string; providerId: string; recommended?: boolean }[];
  onChange: (modelId: string) => void;
  pending: boolean;
}

/**
 * No "when": Claude takes the model at once, Codex from its next turn. `name` is
 * the catalog display name, since `formatModelLabel`'s short id reads badly in
 * a sentence.
 */
function switchSuccessMessage(name: string): string {
  return `Switched to ${name}.`;
}

export interface ModelSwitchInputs {
  canWrite: boolean;
  isReplay: boolean;
}

/**
 * Composer chip wiring for switching a running agent's model. Safe to call
 * before gating on {@link ModelSwitch.canSwitch}.
 */
export function useModelSwitch(agent: Agent, inputs: ModelSwitchInputs): ModelSwitch {
  const { canWrite, isReplay } = inputs;
  const registry = useAgentTypeRegistry();
  const modelSupport = useAgentModelSupportHelpers();
  const updateSettings = useUpdateAgentRuntimeSettings();
  const currentModel = agent.model ?? null;

  // A run's endpoint route is fixed at launch and the model id names it
  // (`vercel/…` ids go through the AI Gateway). The server refuses a switch
  // across routes, so offer only models on the run's own route. `recommended`
  // gives the picker the launch dialog's short list.
  const options = useMemo(() => {
    const agentType = agent.agentType as AgentType;
    if (!modelSupport.getAvailableEntry(agentType)) return [];
    const gatewayRun = isGatewayRoutedModelId(currentModel);
    return modelSupport
      .getDiscoveredModels(agentType)
      .filter((m) => isGatewayRoutedModelId(m.id) === gatewayRun)
      .map((m) => ({
        id: m.id,
        name: m.displayName,
        providerId: m.providerId,
        recommended: m.recommended,
      }));
  }, [modelSupport, agent.agentType, currentModel]);

  const harnessSupports = registry.get(agent.agentType)?.supportsRuntimeModelSwitch === true;
  // A loading or failed catalog is unknown, not empty. Lock the chip without
  // claiming there is no alternative.
  const catalogKnown = !modelSupport.isLoading && !modelSupport.isError;
  const hasAlternative = options.some((o) => o.id !== currentModel);
  const blocked =
    isReplay ||
    !canWrite ||
    !MODEL_SWITCHABLE_STATUSES.has(agent.status) ||
    !harnessSupports ||
    (catalogKnown && !hasAlternative);

  const onChange = useCallback(
    (modelId: string) => {
      if (!modelId || modelId === currentModel) return;
      const name = options.find((o) => o.id === modelId)?.name ?? formatModelLabel(modelId);
      updateSettings.mutate(
        { id: agent.id, model: modelId },
        {
          onSuccess: () => toast.success(switchSuccessMessage(name)),
          onError: (err) => toast.error(modelSwitchErrorMessage(err)),
        },
      );
    },
    [agent.id, currentModel, options, updateSettings],
  );

  return {
    canSwitch: !blocked && catalogKnown,
    unavailableReason: blocked ? MODEL_SWITCH_UNAVAILABLE : undefined,
    options,
    onChange,
    pending: updateSettings.isPending,
  };
}
